import instance from "./instance";
import { ENV } from "@/constants/env";

// PREFIX_PATH 사용 필수 (하드코딩 금지)
const PREFIX_PATH = `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}`;

export interface AdminLoginStatDto {
  admId: number;
  admNm: string;
  email: string;
  admType: string;
  loginCount: number;
  totalSessionTime: number;
  avgSessionTime: number;
  lastLoginDttm: Date | null;
  firstLoginDttm: Date | null;
  isActive: boolean;
}

export interface AdminLoginSummaryDto {
  totalAdmins: number;
  activeAdmins: number;
  totalLogins: number;
  avgSessionTime: number;
  recentActiveAdmins: number;
}

export interface AdminLoginStatsResponseDto {
  summary: AdminLoginSummaryDto;
  admins: AdminLoginStatDto[];
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error: string | null;
}

/**
 * 관리자 로그인 통계 API 클라이언트
 * @description 관리자 로그인 통계 관련 모든 API 호출을 담당합니다.
 */
export const adminLoginStatsApi = {
  /**
   * 도장별 관리자 로그인 통계를 조회합니다.
   * @param comId 도장 ID
   * @returns 관리자 로그인 통계
   */
  async getStats(comId: string): Promise<AdminLoginStatsResponseDto> {
    try {
      // PREFIX_PATH 사용 필수 (하드코딩 금지)
      const apiUrl = `${PREFIX_PATH}/dashboard/admin-login-stats/${comId}`;

      console.log("[API_CALL]", {
        url: apiUrl,
        method: "GET",
        comId,
        PREFIX_PATH,
        baseURL: ENV.API_URL,
        fullUrl: `${ENV.API_URL}/${apiUrl}`,
      });

      // axios instance의 응답 인터셉터가 이미 response.data를 반환하므로
      // 여기서 받는 response는 실제로는 response.data입니다.
      const response = await instance.get<
        ApiResponse<AdminLoginStatsResponseDto>
      >(apiUrl);

      console.log("[API_RESPONSE_RAW]", {
        url: apiUrl,
        responseType: typeof response,
        response: response,
      });

      // axios 인터셉터가 이미 response.data를 반환했으므로
      // response 자체가 ApiResponse<AdminLoginStatsResponseDto> 타입입니다.
      const apiResponse =
        response as unknown as ApiResponse<AdminLoginStatsResponseDto>;

      // 응답 데이터 검증
      if (!apiResponse) {
        throw new Error("No response data received");
      }

      if (!apiResponse.success) {
        throw new Error(
          apiResponse.message || "API request failed - success is false"
        );
      }

      if (!apiResponse.data) {
        throw new Error("No data field in response");
      }

      console.log("[API_RESPONSE_SUCCESS]", {
        url: apiUrl,
        success: apiResponse.success,
        totalAdmins: apiResponse.data?.summary?.totalAdmins,
        adminCount: apiResponse.data?.admins?.length,
        message: apiResponse.message,
      });

      return apiResponse.data;
    } catch (error: any) {
      console.error("[API_ERROR_DETAILED]", {
        comId,
        error: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
        },
        fullError: error,
      });

      // 에러 메시지 구성
      let errorMessage = "Admin login statistics request failed";

      if (error.response) {
        // 서버 응답이 있는 경우
        const status = error.response.status;
        const data = error.response.data;

        if (status === 404) {
          errorMessage = "API endpoint not found. Please check the server.";
        } else if (status === 401) {
          errorMessage = "Authentication required. Please log in.";
        } else if (status === 403) {
          errorMessage = "Access denied. Insufficient permissions.";
        } else if (status >= 500) {
          errorMessage = "Server error occurred. Please try again later.";
        } else if (data?.message) {
          errorMessage = data.message;
        }
      } else if (error.request) {
        // 요청은 보냈지만 응답을 받지 못한 경우
        errorMessage = "Network error. Please check your connection.";
      } else if (error.message) {
        // 기타 에러
        errorMessage = error.message;
      }

      throw new Error(errorMessage);
    }
  },
};
